import { QueryParams } from '../types';

/**
 * Serializes a key for query string serialization.
 * @param key - The key to serialize.
 * @param parentKey - The parent key, if any.
 * @returns The serialized key.
 */
function qsSerializeKey(key: string | number, parentKey: string = ''): string {
  return parentKey ? `${parentKey}[${key}]` : String(key);
}

/**
 * Serializes a parameter for query string serialization.
 * @param key - The key of the parameter.
 * @param value - The value of the parameter.
 * @param serializedParams - The array of serialized parameters.
 * @param parentKey - The parent key, if any.
 * @returns The updated array of serialized parameters.
 */
function qsSerializeParam(
  key: string | number,
  value: {} | unknown,
  serializedParams: { key: string, value: unknown }[] = [],
  parentKey: string = ''
): { key: string, value: unknown }[] {
  if (value != null) {
    if (typeof value === 'object') {
      serializedParams = qsSerializeParams(value, serializedParams, qsSerializeKey(key, parentKey));
    } else {
      serializedParams.push({
        key: qsSerializeKey(key, parentKey),
        value: value,
      });
    }
  }
  return serializedParams;
}

/**
 * Serializes parameters for query string serialization.
 * @param params - The parameters to serialize.
 * @param serializedParams - The array of serialized parameters.
 * @param parentKey - The parent key, if any.
 * @returns The updated array of serialized parameters.
 */
function qsSerializeParams(
  params: {},
  serializedParams: { key: string, value: unknown }[] = [],
  parentKey: string = ''
): { key: string, value: unknown }[] {
  if (Array.isArray(params)) {
    params.forEach((value, key) => {
      serializedParams = qsSerializeParam(key, value, serializedParams, parentKey);
    });
  } else {
    Object.entries(params).forEach(([key, value]) => {
      serializedParams = qsSerializeParam(key, value, serializedParams, parentKey);
    });
  }
  return serializedParams;
}

/**
 * Serializes query parameters into a query string.
 * @param queryParams - The query parameters to serialize.
 * @returns The serialized query string.
 */
export function qsSerialize(queryParams: QueryParams = {}): string {
  const params = qsSerializeParams(queryParams);
  if (params.length) {
    return params.map(x => `${x.key}=${x.value}`).join('&');
  }
  return '';
}

/**
 * Serializes query parameters into a raw query string.
 * @param queryParams - The query parameters to serialize.
 * @returns The serialized raw query string.
 */
export function qsSerializeRaw(queryParams: {} = {}): string {
  const params = qsSerializeParams(queryParams);
  if (params.length) {
    return params.map(x => `${x.key}=${x.value}`).join('&');
  }
  return '';
}

/**
 * Deserializes a value from a query string.
 * @param value - The value to deserialize.
 * @returns The deserialized value.
 */
function qsDeserializeValue(value: string): string | boolean | number {
  if (String(parseInt(value)) === value) {
    return Number(value);
  } else if (String(value) === 'true') {
    return true;
  } else if (String(value) === 'false') {
    return false;
  }
  return value;
}

/**
 * Deserializes a key from a query string.
 * @param value - The key to deserialize.
 * @returns The deserialized key.
 */
function qsDeserializeKey(value: string): string | number {
  if (String(parseInt(value)) === value) {
    return Number(value);
  }
  return value;
}

/**
 * Deserializes keys from a query string.
 * @param key - The key to deserialize.
 * @returns The deserialized keys.
 */
function qsDeserializeKeys(key: string): (string | number)[] {
  return key
    .replace(/[\]]/g, '')
    .split('[')
    .map((value) => qsDeserializeKey(value))
    .filter((value) => value !== '');
}

/**
 * Gets the next key item for deserialization.
 * @param key - The current key.
 * @returns The next key item.
 */
function getNextKeyItem(key: string | number | undefined): { [key: string]: unknown } | unknown[] {
  if (typeof key === 'number') {
    return [];
  } else {
    return {};
  }
}

/**
 * Deserializes a parameter from a query string.
 * @param key - The key of the parameter.
 * @param value - The value of the parameter.
 * @param deserializedParams - The deserialized parameters.
 * @returns The updated deserialized parameters.
 */
function qsDeserializeParam(key: string, value: string, deserializedParams: {} = {}): {} {
  const keys = qsDeserializeKeys(key);
  let target: { [key: string]: unknown } | unknown[] = deserializedParams;
  while (keys.length) {
    const key = keys.shift() as string | number;
    if (keys.length === 0) {
      (target as any)[key] = qsDeserializeValue(value);
    } else {
      const next: { [key: string]: unknown } | unknown[] =
        (target as any)[key] ?
          (target as any)[key] :
          (target as any)[key] = getNextKeyItem(keys[0]);
      target = next;
    }
  }
  return deserializedParams;
}

/**
 * Deserializes parameters from a query string.
 * @param params - The parameters to deserialize.
 * @param deserializedParams - The deserialized parameters.
 * @returns The updated deserialized parameters.
 */
function qsDeserializeParams(params: string[][], deserializedParams: {} = {}): {} {
  params.forEach((x) => qsDeserializeParam(x[0]!, x[1]!, deserializedParams));
  return deserializedParams;
}

/**
 * Deserializes a query string into query parameters.
 * @param query - The query string to deserialize.
 * @param deserializedParams - The deserialized query parameters.
 * @returns The deserialized query parameters.
 */
export function qsDeserialize(query: string = '', deserializedParams: {} = {}): QueryParams {
  if (query.indexOf('?') === 0) {
    query = query.substring(1);
  }
  if (query !== '') {
    const params = query.split('&').map(x => x.split('='));
    if (params.length) {
      return qsDeserializeParams(params, deserializedParams);
    }
  }
  return deserializedParams;
}

/**
 * Deserializes a raw query string into parameters.
 * @param query - The raw query string to deserialize.
 * @param deserializedParams - The deserialized parameters.
 * @returns The deserialized parameters.
 */
export function qsDeserializeRaw(query: string = '', deserializedParams: {} = {}): {} {
  if (query.indexOf('?') === 0) {
    query = query.substring(1);
  }
  if (query !== '') {
    const params = query.split('&').map(x => x.split('='));
    if (params.length) {
      return qsDeserializeParams(params, deserializedParams);
    }
  }
  return deserializedParams;
}
